// Orchestrator: wires capabilities → MediaPipe tracker → camera → renderer into
// a single per-frame loop, runs the state machine, and emits state/ready/capture
// events to the UI. Everything heavy (MediaPipe, Three) is dynamically imported
// so nothing loads until start() is called on button click.

import {
  DEFAULT_CONFIG,
  EMPTY_MEASUREMENT,
  type EngineConfig,
  type EngineEvent,
  type EngineListener,
  type EngineState,
  type FrameAsset,
  type QualityHint,
  type TryOnMode,
} from './types';
import { detectCapabilities, type Capabilities } from './capabilities';
import { startCamera, CameraError, type CameraHandle } from './camera';
import { createFaceTracker, type FaceTracker } from './faceTracker';
import { MeasurementAccumulator, measureFrame } from './measure';
import { FaceShapeVoter, classifyFaceShape, type FaceShapeResult } from './faceShape';
import { IDX, type LM } from './landmarks';

const SEARCH_GRACE_MS = 600; // keep "tracking" briefly through dropped frames

export interface StartArgs {
  canvas: HTMLCanvasElement;
  video: HTMLVideoElement;
  frame: FrameAsset;
}

export class TryOnEngine {
  readonly config: EngineConfig;
  private listeners = new Set<EngineListener>();

  private caps: Capabilities | null = null;
  private camera: CameraHandle | null = null;
  private tracker: FaceTracker | null = null;
  private renderer: import('./renderer').GlassesRenderer | null = null;

  private accumulator = new MeasurementAccumulator();
  private shapeVoter = new FaceShapeVoter();
  private shapeResult: FaceShapeResult | null = null;

  private rafId = 0;
  private usingRVFC = false;
  private lastTs = 0;
  private lastFaceTs = 0;
  private fpsEma = 0;
  private frameInterval: number;
  private lumCanvas: HTMLCanvasElement | null = null;

  private state: EngineState = {
    status: 'idle',
    hint: 'ok',
    error: null,
    fps: 0,
    measurement: EMPTY_MEASUREMENT,
  };

  constructor(opts: { mode: TryOnMode } & Partial<EngineConfig>) {
    this.config = { ...DEFAULT_CONFIG, ...opts };
    this.frameInterval = 1000 / this.config.targetFps;
  }

  on(listener: EngineListener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private emit(event: EngineEvent) {
    for (const l of this.listeners) l(event);
  }

  private patch(partial: Partial<EngineState>) {
    this.state = { ...this.state, ...partial };
    this.emit({ type: 'state', state: this.state });
  }

  getCapabilities(): Capabilities {
    if (!this.caps) this.caps = detectCapabilities();
    return this.caps;
  }

  getFaceShape(): FaceShapeResult | null {
    return this.shapeResult;
  }

  async start({ canvas, video, frame }: StartArgs): Promise<void> {
    const caps = this.getCapabilities();
    if (!caps.supported) {
      this.patch({
        status: 'error',
        error: {
          code: caps.reason ?? 'unsupported',
          messageKey: `error.${caps.reason ?? 'unsupported'}`,
          recoverable: false,
        },
      });
      return;
    }

    // Resolve 'auto' delegate from device tier.
    if (this.config.delegate === 'auto') {
      (this.config as EngineConfig).delegate = caps.recommendedDelegate;
    }

    try {
      this.patch({ status: 'loading' });
      this.tracker = await createFaceTracker(this.config);

      const { GlassesRenderer } = await import('./renderer');
      this.renderer = new GlassesRenderer(canvas, this.config);
      await this.renderer.loadFrame(frame);

      this.patch({ status: 'starting' });
      this.camera = await startCamera(video, caps.captureResolution);
      this.renderer.setVideo(video);
      this.syncSize(canvas, video);

      this.patch({ status: 'searching' });
      this.emit({ type: 'ready' });
      this.beginLoop(video);
    } catch (err) {
      const code =
        err instanceof CameraError
          ? err.code
          : ('sdk-load-failed' as const);
      this.patch({
        status: 'error',
        error: { code, messageKey: `error.${code}`, recoverable: code !== 'sdk-load-failed' },
      });
      this.stop();
    }
  }

  async setFrame(frame: FrameAsset, variantGlbUrl?: string): Promise<void> {
    await this.renderer?.loadFrame(frame, variantGlbUrl);
  }

  capture(): void {
    const dataUrl = this.renderer?.capture();
    if (dataUrl) this.emit({ type: 'capture', dataUrl });
  }

  private syncSize(canvas: HTMLCanvasElement, video: HTMLVideoElement) {
    const rect = canvas.getBoundingClientRect();
    this.renderer?.resize(
      rect.width || video.videoWidth,
      rect.height || video.videoHeight,
      video.videoWidth || 640,
      video.videoHeight || 480,
    );
  }

  private beginLoop(video: HTMLVideoElement) {
    const supportsRVFC = 'requestVideoFrameCallback' in HTMLVideoElement.prototype;
    this.usingRVFC = supportsRVFC;

    const tick = (now: number) => {
      if (!this.tracker || !this.renderer) return;
      // Throttle to target FPS.
      if (now - this.lastTs < this.frameInterval - 1) {
        this.schedule(video, tick);
        return;
      }
      const dt = now - this.lastTs;
      this.lastTs = now;
      if (dt > 0) {
        const inst = 1000 / dt;
        this.fpsEma = this.fpsEma ? this.fpsEma * 0.9 + inst * 0.1 : inst;
      }

      this.processFrame(video, now);
      this.schedule(video, tick);
    };

    this.schedule(video, tick);
  }

  private schedule(video: HTMLVideoElement, tick: (t: number) => void) {
    if (document.hidden) {
      // Pause work while tab is backgrounded; resume on visibility.
      const onVisible = () => {
        document.removeEventListener('visibilitychange', onVisible);
        this.schedule(video, tick);
      };
      document.addEventListener('visibilitychange', onVisible);
      return;
    }
    if (this.usingRVFC) {
      this.rafId = video.requestVideoFrameCallback((t) => tick(t));
    } else {
      this.rafId = requestAnimationFrame(tick);
    }
  }

  private processFrame(video: HTMLVideoElement, now: number) {
    if (video.readyState < 2 || !this.tracker || !this.renderer) return;

    // MediaPipe requires strictly increasing timestamps.
    const ts = Math.max(now, this.lastFaceTs + 1);
    this.lastFaceTs = ts;

    const { landmarks, faceCount } = this.tracker.detect(video, ts);
    this.renderer.update(landmarks, ts);
    this.renderer.render();

    let hint: QualityHint = 'ok';
    let status = this.state.status;

    if (!landmarks) {
      // Hold "tracking" through brief dropouts to avoid UI flicker.
      if (now - this.lastFaceSeen > SEARCH_GRACE_MS) {
        status = 'searching';
        hint = 'no-face';
      }
    } else {
      this.lastFaceSeen = now;
      status = 'tracking';
      hint = this.qualityHint(landmarks, faceCount, video);
      if (hint === 'ok') this.sampleFit(landmarks, video, now);
    }

    const measurement = this.accumulator.read();
    this.patch({ status, hint, fps: Math.round(this.fpsEma), measurement });
  }

  private lastFaceSeen = 0;
  private lastShapeSample = 0;

  private qualityHint(lm: LM[], faceCount: number, video: HTMLVideoElement): QualityHint {
    if (faceCount > 1) return 'multi-face';

    const aspect = (video.videoWidth || 1) / (video.videoHeight || 1);
    const eyeSpan = Math.hypot(
      (lm[IDX.rightEyeOuter].x - lm[IDX.leftEyeOuter].x) * aspect,
      lm[IDX.rightEyeOuter].y - lm[IDX.leftEyeOuter].y,
    );
    if (eyeSpan < 0.12) return 'too-far';
    if (eyeSpan > 0.42) return 'too-close';

    // Yaw/pitch proxy: how much the eye axis tips into depth.
    const yaw = Math.abs(lm[IDX.rightEyeOuter].z - lm[IDX.leftEyeOuter].z) / (eyeSpan || 1);
    const pitch = Math.abs(lm[IDX.foreheadTop].z - lm[IDX.chin].z) / (eyeSpan || 1);
    if (Math.max(yaw, pitch) > 1.6) return 'extreme-angle';

    if (this.isLowLight(video)) return 'low-light';
    return 'ok';
  }

  private sampleFit(lm: LM[], video: HTMLVideoElement, now: number) {
    const w = video.videoWidth || 640;
    const h = video.videoHeight || 480;
    const m = measureFrame(lm, w, h);
    if (m) this.accumulator.push(m);

    // Face shape is stable; sample a few times a second and take the mode.
    if (now - this.lastShapeSample > 250) {
      this.lastShapeSample = now;
      this.shapeVoter.add(classifyFaceShape(lm, w, h).shape);
      this.shapeResult = this.shapeVoter.result();
    }
  }

  private isLowLight(video: HTMLVideoElement): boolean {
    // Cheap luminance sample from a tiny downscale; runs every frame but is 16×16.
    if (!this.lumCanvas) {
      this.lumCanvas = document.createElement('canvas');
      this.lumCanvas.width = 16;
      this.lumCanvas.height = 16;
    }
    const ctx = this.lumCanvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) return false;
    try {
      ctx.drawImage(video, 0, 0, 16, 16);
      const { data } = ctx.getImageData(0, 0, 16, 16);
      let sum = 0;
      for (let i = 0; i < data.length; i += 4) {
        sum += 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
      }
      return sum / (data.length / 4) < 38;
    } catch {
      return false; // cross-origin/tainted — skip the check
    }
  }

  stop(): void {
    if (this.rafId) {
      if (this.usingRVFC && this.camera?.video) {
        this.camera.video.cancelVideoFrameCallback?.(this.rafId);
      } else {
        cancelAnimationFrame(this.rafId);
      }
      this.rafId = 0;
    }
    this.tracker?.close();
    this.tracker = null;
    this.renderer?.dispose();
    this.renderer = null;
    this.camera?.stop();
    this.camera = null;
    this.accumulator.reset();
    this.shapeVoter.reset();
    this.shapeResult = null;
    if (this.state.status !== 'error') this.patch({ status: 'idle' });
  }
}
